import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import * as LucideIcons from "lucide-react";
import {
  LayoutDashboard,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Search,
  ArrowLeft,
  X,
  Code,
  Eye,
  ChevronRight,
  ChevronDown,
  Check,
  Info,
  Grid,
  Loader2,
  Activity,
  Hash,
  Gauge,
  PieChart,
  Disc,
  BarChart2,
  AlignLeft,
  LineChart,
  AreaChart,
  Grid3X3,
  Table,
  CircleEllipsis,
} from "lucide-react";

export function DynamicLucideIcon({
  name,
  className = "w-4 h-4",
}: {
  name?: string;
  className?: string;
}) {
  if (!name || typeof name !== "string") return null;
  const iconName = name.trim();
  if (!iconName) return null;

  let IconComponent = (LucideIcons as Record<string, any>)[iconName];
  if (!IconComponent) {
    const pascalName = iconName.replace(/(?:^|-|_)+(\w)/g, (_, c) => c.toUpperCase());
    IconComponent = (LucideIcons as Record<string, any>)[pascalName];
  }

  if (!IconComponent || (typeof IconComponent !== "function" && typeof IconComponent !== "object")) {
    return null;
  }

  return <IconComponent className={className} />;
}

// const POPULAR_LUCIDE_ICONS = [
//   "Activity",
//   "AlertCircle",
//   "AlertTriangle",
//   "Archive",
//   "BarChart",
//   "BarChart2",
//   "Bell",
//   "Bookmark",
//   "Box",
//   "Bug",
//   "Building",
//   "Calendar",
//   "Camera",
//   "CheckCircle",
//   "Clock",
//   "Cloud",
//   "Code",
//   "Cpu",
//   "Database",
//   "Download",
//   "Eye",
//   "FileText",
//   "Filter",
//   "Folder",
//   "Globe",
//   "Grid",
//   "HardDrive",
//   "Heart",
//   "HelpCircle",
//   "Home",
//   "Info",
//   "Key",
//   "Layers",
//   "Layout",
//   "Link",
//   "List",
//   "Lock",
//   "Mail",
//   "MapPin",
//   "Monitor",
//   "Network",
//   "Package",
//   "PieChart",
//   "Radio",
//   "RefreshCw",
//   "Search",
//   "Server",
//   "Settings",
//   "Share2",
//   "Shield",
//   "ShieldAlert",
//   "ShieldCheck",
//   "Smartphone",
//   "Terminal",
//   "TrendingUp",
//   "TrendingDown",
//   "User",
//   "Users",
//   "Wifi",
//   "Zap",
// ];

function IconPickerPopover({
  value,
  onChange,
}: {
  value: string;
  onChange: (iconName: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayLimit, setDisplayLimit] = useState(120);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allIconNames = useMemo(() => {
    return Object.keys(LucideIcons)
      .filter((key) => {
        return (
          key !== "default" &&
          key !== "createLucideIcon" &&
          !key.startsWith("Lucide") &&
          !key.endsWith("Icon") &&
          /^[A-Z]/.test(key)
        );
      })
      .sort();
  }, []);

  const matchingIcons = useMemo(() => {
    if (!searchTerm.trim()) {
      return allIconNames;
    }
    const term = searchTerm.toLowerCase().replace(/[^a-z0-9]/g, "");
    return allIconNames.filter((name) => name.toLowerCase().includes(term));
  }, [searchTerm, allIconNames]);

  const displayedIcons = useMemo(() => {
    return matchingIcons.slice(0, displayLimit);
  }, [matchingIcons, displayLimit]);

  const handleGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      if (displayLimit < matchingIcons.length) {
        setDisplayLimit((prev) => prev + 120);
      }
    }
  };

  return (
    <div className="relative inline-block text-left" ref={popoverRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setDisplayLimit(120);
        }}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-600 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors shadow-sm focus:outline-none"
        title={value ? `Icon: ${value}` : "Select Header Icon"}
      >
        {value ? (
          <>
            <DynamicLucideIcon name={value} className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0" />
            <span className="text-gray-900 dark:text-white max-w-[90px] truncate">{value}</span>
          </>
        ) : (
          <span className="text-gray-500 dark:text-gray-400 text-[11px]">＋ Icon</span>
        )}
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 z-[120] w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-3 flex flex-col gap-2.5 animate-fade-in">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2">
            <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Choose Icon ({matchingIcons.length})
            </span>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
                className="text-[10px] text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 font-semibold underline"
              >
                Clear Icon
              </button>
            )}
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={`Search ${allIconNames.length}+ icons (e.g. Shield, Cpu)...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setDisplayLimit(120);
              }}
              className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white pl-8 pr-3 py-1.5 focus:outline-none focus:border-orange-500 placeholder-gray-400 dark:placeholder-gray-500 font-medium"
              autoFocus
            />
          </div>

          <button
            type="button"
            onClick={() => {
              onChange("");
              setIsOpen(false);
            }}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${!value
                ? "bg-orange-500/10 dark:bg-orange-500/15 border-orange-500/50 dark:border-orange-500/60 text-orange-600 dark:text-orange-400 font-bold"
                : "border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-700 hover:text-gray-900 dark:hover:text-white"
              }`}
          >
            <span>No Icon (Default)</span>
            {!value && <Check className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />}
          </button>

          <div
            onScroll={handleGridScroll}
            className="max-h-56 overflow-y-auto custom-scroll grid grid-cols-6 gap-1.5 p-1 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800"
          >
            {displayedIcons.map((iconName) => {
              const isSelected = value === iconName;
              return (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => {
                    onChange(iconName);
                    setIsOpen(false);
                  }}
                  title={iconName}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${isSelected
                      ? "bg-orange-500/15 dark:bg-orange-500/25 border-orange-500 text-orange-600 dark:text-orange-400 shadow-md shadow-orange-500/10 scale-105"
                      : "border-transparent hover:bg-gray-200/60 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                >
                  <DynamicLucideIcon name={iconName} className="w-4 h-4" />
                </button>
              );
            })}
          </div>
          {matchingIcons.length === 0 && (
            <p className="text-[11px] text-gray-500 text-center py-3">No matching icons found</p>
          )}
          {displayedIcons.length < matchingIcons.length && (
            <p className="text-[10px] text-gray-500 text-center italic">
              Showing {displayedIcons.length} of {matchingIcons.length} icons — scroll for more
            </p>
          )}
        </div>
      )}
    </div>
  );
}
import baseapi from "../../services/baseapi";
import { showSuccess, showError } from "../../utils/toast";
import WidgetRendererV2 from "./WidgetRendererV2";
import { useClientLevel } from "../../hooks/useClientLevel";
import { Pagination } from "../InternalScan/InternalScanDashboard/Pagination";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { hasPermission } from "../../utils/permissionsHelper";
import ColumnVisibilityDropdown, { type ColumnOption } from "../../components/ColumnVisibilityDropdown";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface AnalyticsDashboard {
  id: number;
  name: string;
  description: string;
  widget_count: number;
  is_public: boolean;
  owner_user_id: string;
  can_edit: boolean;
}

// ─── Column Visibility Options for Dashboards List ───
type DashboardColumnKey = "name" | "description" | "widgets" | "actions";

const DASHBOARD_COLUMN_OPTIONS: ColumnOption<DashboardColumnKey>[] = [
  { key: "name", label: "Name", icon: LayoutDashboard, color: "text-orange-400" },
  { key: "description", label: "Description", icon: AlignLeft, color: "text-blue-400" },
  { key: "widgets", label: "Widgets", icon: Grid, color: "text-emerald-400" },
  { key: "actions", label: "Actions", icon: Code, color: "text-purple-400" },
];

// ─── Constants ───
const RANGES = [
  { key: "", label: "All time" },
  { key: "24h", label: "Last 24 hours", ms: 864e5 },
  { key: "7d", label: "Last 7 days", ms: 7 * 864e5 },
  { key: "30d", label: "Last 30 days", ms: 30 * 864e5 },
  { key: "90d", label: "Last 90 days", ms: 90 * 864e5 },
];

const CHART_ICONS: Record<string, React.ComponentType<any>> = {
  metric: Hash,
  gauge: Gauge,
  pie: PieChart,
  donut: Disc,
  bar: BarChart2,
  horizontal_bar: AlignLeft,
  line: LineChart,
  area: AreaChart,
  heatmap: Grid3X3,
  table: Table,
};

const CHART_COLORS: Record<string, string> = {
  metric: "text-blue-400",
  gauge: "text-emerald-400",
  pie: "text-pink-400",
  donut: "text-purple-400",
  bar: "text-orange-400",
  horizontal_bar: "text-amber-400",
  line: "text-sky-400",
  area: "text-teal-400",
  heatmap: "text-red-400",
  table: "text-indigo-400",
};

const AGG_LABELS: Record<string, string> = {
  count: "Count of rows",
  distinct_count: "Unique count",
  sum: "Sum",
  avg: "Average",
  min: "Minimum",
  max: "Maximum",
  median: "Median",
  percentiles: "Percentiles",
  std_deviation: "Std deviation",
};

const OP_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  contains: "contains",
  not_contains: "doesn't contain",
  starts_with: "starts with",
  ends_with: "ends with",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "is one of",
  not_in: "is not one of",
  between: "between",
  exists: "has a value",
  not_exists: "is empty",
  has: "includes",
  not_has: "doesn't include",
};

// ─── Custom Select Component ───
function CustomSelect({
  value,
  options,
  onChange,
  placeholder = "Select option...",
  className = "",
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className={`relative flex-1 ${isOpen ? "z-[100]" : "z-10"} ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-600 text-left text-xs text-gray-900 dark:text-white px-3 py-2 rounded-lg font-semibold truncate flex items-center justify-between shadow-sm focus:outline-none transition-colors"
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "-rotate-90" : "rotate-90"}`} />
      </button>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] max-h-[220px] overflow-y-auto custom-scroll p-1 flex flex-col gap-0.5 animate-fade-in">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setIsOpen(false);
              }}
              className={`w-full text-left text-xs px-3 py-1.5 rounded transition-colors flex items-center justify-between ${
                o.value === value ? "bg-orange-500/10 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 font-bold" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && <Check className="w-3 h-3 text-orange-500 dark:text-orange-400 shrink-0 ml-1.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CustomDashboardV2({
  dashboardId: propDashboardId,
  readOnly = false,
}: {
  dashboardId?: number;
  readOnly?: boolean;
}) {
  const level = useClientLevel();

  const canRead = hasPermission("cdash", "read");
  const canCreate = hasPermission("cdash", "create");
  const canUpdate = hasPermission("cdash", "update");
  const canDelete = hasPermission("cdash", "delete");



  // View state
  const [view, setView] = useState<"list" | "dashboard">(propDashboardId ? "dashboard" : "list");
  const [dashboardId, setDashboardId] = useState<number | null>(propDashboardId ?? null);

  useEffect(() => {
    if (propDashboardId) {
      setDashboardId(propDashboardId);
      setView("dashboard");
    }
  }, [propDashboardId]);

  // Dashboards list state
  const [dashboards, setDashboards] = useState<AnalyticsDashboard[]>([]);
  const [totalDashboards, setTotalDashboards] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Column Visibility State
  const [visibleColumns, setVisibleColumns] = useState<Record<DashboardColumnKey, boolean>>({
    name: true,
    description: true,
    widgets: true,
    actions: true,
  });

  const visibleColumnCount = useMemo(() => {
    return Object.values(visibleColumns).filter(Boolean).length;
  }, [visibleColumns]);

  // Dashboard detail/canvas state
  const [dashboard, setDashboard] = useState<any>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [rangeKey, _setRangeKey] = useState<string>("");
  const [widgetResults, setWidgetResults] = useState<Record<number, any>>({});
  const [widgetErrors, setWidgetErrors] = useState<Record<number, string>>({});
  const [widgetLoading, setWidgetLoading] = useState<Record<number, boolean>>({});

  // Layout states for dashboard widgets
  const [widgetLayouts, setWidgetLayouts] = useState<Record<number, { x: number; y: number; w: number; h: number }>>({});

  // Builder Modal State
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<any | null>(null);

  // Create Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");

  // Rename Modal State
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameTargetId, setRenameTargetId] = useState<number | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");

  // Actions dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 110) });
    setOpenMenuId((prev) => (prev === String(id) ? null : String(id)));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest("[data-action-menu]")) {
        setOpenMenuId(null);
      }
    };
    const handleScroll = () => {
      if (openMenuId) setOpenMenuId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuId]);

  // Share Modal State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUserId, setShareUserId] = useState("");

  // Delete Confirmation Modal State
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "dashboard" | "widget";
    id: number;
    name: string;
  } | null>(null);

  const COLS = 12;

  // Fetch dashboards with API-level pagination (skip & limit)
  const fetchDashboards = async (
    page = currentPage,
    limit = itemsPerPage,
    searchTermValue = searchTerm
  ) => {
    setLoadingList(true);
    try {
      const skip = (page - 1) * limit;
      const params: any = {
        skip,
        limit,
      };
      const queryObj: any = {};
      if (level && level.name !== "global" && level.id) {
        queryObj.client_id = level.id;
      }
      if (searchTermValue) {
        queryObj.$or = [
          { name: { $ilike: `%${searchTermValue}%` } },
          { description: { $ilike: `%${searchTermValue}%` } },
        ];
      }
      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }
      const res = await baseapi.get("/cdash/dashboards", { params });
      let resData = res.data;
      let rows: AnalyticsDashboard[] = [];
      let total = 0;

      if (resData && resData.status !== undefined) {
        rows = Array.isArray(resData.data) ? resData.data : [];
        total = resData.total ?? resData.count ?? rows.length;
      } else if (Array.isArray(resData)) {
        rows = resData;
        total = resData.length;
      }

      setDashboards(rows);
      setTotalDashboards(total);
    } catch (e: any) {
      showError(e.message || "Failed to load dashboards");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (view === "list") {
      fetchDashboards(currentPage, itemsPerPage, searchTerm);
    }
  }, [view, level.id, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    if (!propDashboardId) {
      setView("list");
      setDashboardId(null);
      setDashboard(null);
    }
  }, [level.id, propDashboardId]);



  // Convert rangeKey to time ISO strings
  const getRangeParams = (key: string) => {
    const r = RANGES.find((x) => x.key === key);
    if (!r?.ms) return {};
    return { time_from: new Date(Date.now() - r.ms).toISOString() };
  };

  // Fetch individual dashboard details + execute widgets
  const fetchDashboardDetails = async (id: number) => {
    setLoadingDashboard(true);
    try {
      const res = await baseapi.get(`/cdash/dashboards/${id}`);
      let data = res.data;
      if (data && data.status !== undefined && data.data !== undefined) {
        data = data.status ? data.data : null;
      }
      if (data) {
        if (Array.isArray(data.widgets)) {
          data.widgets = data.widgets.map((w: any) => {
            let parsedLayout = w.layout;
            if (typeof w.layout === "string") {
              try {
                parsedLayout = JSON.parse(w.layout);
              } catch {
                parsedLayout = null;
              }
            }
            let parsedQdef = w.query_definition;
            if (typeof w.query_definition === "string") {
              try {
                parsedQdef = JSON.parse(w.query_definition);
              } catch {
                parsedQdef = null;
              }
            }
            let parsedVis = w.visualization_config;
            if (typeof w.visualization_config === "string") {
              try {
                parsedVis = JSON.parse(w.visualization_config);
              } catch {
                parsedVis = null;
              }
            }
            return {
              ...w,
              layout: parsedLayout,
              query_definition: parsedQdef,
              visualization_config: parsedVis,
            };
          });
        } else {
          data.widgets = [];
        }

        setDashboard(data);

        // Initialize layouts
        const layouts: Record<number, any> = {};
        (data.widgets || []).forEach((w: any, idx: number) => {
          layouts[w.id] = {
            x: Math.min(COLS - 1, w.layout?.x ?? (idx % 2) * 6),
            y: w.layout?.y ?? Math.floor(idx / 2) * 4,
            w: Math.max(2, Math.min(COLS, w.layout?.w ?? 6)),
            h: Math.max(2, w.layout?.h ?? 4),
          };
        });
        setWidgetLayouts(layouts);

        // Load widget data
        (data.widgets || []).forEach((w: any) => {
          loadWidgetData(id, w.id, undefined, undefined, w);
        });
      } else {
        setDashboard(null);
      }
    } catch (e: any) {
      showError(e.message || "Failed to load dashboard details");
    } finally {
      setLoadingDashboard(false);
    }
  };

  const loadWidgetData = async (
    dashId: number,
    widgetId: number,
    skip?: number,
    limit?: number,
    widgetObj?: any
  ) => {
    setWidgetLoading((prev) => ({ ...prev, [widgetId]: true }));
    setWidgetErrors((prev) => ({ ...prev, [widgetId]: "" }));
    try {
      const params: any = getRangeParams(rangeKey);
      if (level && level.name !== "global" && level.id) {
        params.client_id = level.id;
      }

      const targetWidget = widgetObj || dashboard?.widgets?.find((w: any) => w.id === widgetId);
      const isTable =
        targetWidget?.chart_type === "table" ||
        targetWidget?.query_def?.chart_type === "table" ||
        targetWidget?.query_definition?.chart_type === "table";

      let bodyPayload: any = null;
      if (isTable) {
        const s = skip ?? 0;
        const l = limit ?? 10;
        params.skip = s;
        params.limit = l;
        bodyPayload = { skip: s, limit: l };
      }

      const res = await baseapi.post(`/cdash/dashboards/${dashId}/widgets/${widgetId}/execute`, bodyPayload, {
        params,
      });
      const responcedata = res.data;
      const data = responcedata.data;
      if (responcedata.status) {
        setWidgetResults((prev) => ({ ...prev, [widgetId]: data }));
      } else {
        setWidgetErrors((prev) => ({ ...prev, [widgetId]: 'Api Data is Not Status = false ' }));
      }
    } catch (e: any) {
      const errMsg = e.response?.data?.detail
        ? typeof e.response.data.detail === "string"
          ? e.response.data.detail
          : JSON.stringify(e.response.data.detail)
        : e.message || "Execution error";
      setWidgetErrors((prev) => ({ ...prev, [widgetId]: errMsg }));
    } finally {
      setWidgetLoading((prev) => ({ ...prev, [widgetId]: false }));
    }
  };

  useEffect(() => {
    if (view === "dashboard" && dashboardId !== null) {
      fetchDashboardDetails(dashboardId);
    }
  }, [view, dashboardId, rangeKey]);

  const refreshAll = () => {
    if (!dashboard || !Array.isArray(dashboard.widgets)) return;
    dashboard.widgets.forEach((w: any) => {
      loadWidgetData(dashboard.id, w.id, undefined, undefined, w);
    });
  };

  // Create dashboard Submit
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) {
      showError("You do not have permission to create dashboards.");
      return;
    }
    if (!createName.trim()) return;
    try {
      const payload: any = {
        name: createName.trim(),
        description: createDesc.trim(),
      };
      if (level && level.name !== "global" && level.id) {
        payload.client_id = level.id;
      }
      const res = await baseapi.post(
        "/cdash/dashboards",
        payload
      );
      showSuccess("Dashboard created");
      setDashboardId(res.data.id);
      setView("dashboard");
      setIsCreateModalOpen(false);
    } catch (e: any) {
      showError(e.message || "Failed to create dashboard");
    }
  };

  // Delete dashboard
  const handleDeleteDashboard = (id: number, name: string) => {
    setDeleteTarget({ type: "dashboard", id, name });
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (!canDelete) {
      showError("You do not have permission to delete dashboards or widgets.");
      return;
    }
    const { type, id } = deleteTarget;
    setDeleteTarget(null);
    if (type === "dashboard") {
      try {
        await baseapi.delete(`/cdash/dashboards/${id}`);
        showSuccess("Dashboard deleted");
        setView("list");
        fetchDashboards();
      } catch (e: any) {
        showError(e.message || "Failed to delete dashboard");
      }
    } else if (type === "widget") {
      try {
        await baseapi.delete(`/cdash/dashboards/${dashboard.id}/widgets/${id}`);
        showSuccess("Widget deleted");
        fetchDashboardDetails(dashboard.id);
      } catch (e: any) {
        showError(e.message || "Failed to delete widget");
      }
    }
  };

  // Duplicate dashboard
  // const handleDuplicateDashboard = async (id: number) => {
  //   try {
  //     const res = await baseapi.post(`/cdash/dashboards/${id}/clone`, null);
  //     showSuccess("Dashboard cloned successfully");
  //     setDashboardId(res.data.id);
  //     setView("dashboard");
  //   } catch (e: any) {
  //     showError(e.message || "Failed to clone dashboard");
  //   }
  // };

  // Rename dashboard Submit
  const openRenameModalFor = (d: { id: number; name: string; description?: string }) => {
    setRenameTargetId(d.id);
    setRenameName(d.name || "");
    setRenameDesc(d.description || "");
    setIsRenameModalOpen(true);
  };

  const openRenameModal = () => {
    if (!dashboard) return;
    openRenameModalFor(dashboard);
  };

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpdate) {
      showError("You do not have permission to update dashboards.");
      return;
    }
    if (!renameName.trim()) return;
    const targetId = renameTargetId || dashboard?.id;
    if (!targetId) return;
    try {
      await baseapi.put(
        `/cdash/dashboards/${targetId}`,
        { name: renameName.trim(), description: renameDesc.trim() }
      );
      showSuccess("Dashboard updated");
      if (dashboard && dashboard.id === targetId) {
        setDashboard((prev: any) => ({
          ...prev,
          name: renameName.trim(),
          description: renameDesc.trim(),
        }));
      }
      setIsRenameModalOpen(false);
      fetchDashboards();
    } catch (e: any) {
      showError(e.message || "Failed to update dashboard");
    }
  };

  // // Share dashboard Submit
  // const openShareModal = () => {
  //   setShareUserId("");
  //   setIsShareModalOpen(true);
  // };

  const handleShareSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareUserId.trim()) return;
    try {
      await baseapi.post(
        `/cdash/dashboards/${dashboard.id}/share`,
        { user_id: shareUserId.trim(), permission: "view" }
      );
      showSuccess(`Dashboard shared with ${shareUserId.trim()}`);
      setIsShareModalOpen(false);
    } catch (e: any) {
      showError(e.message || "Failed to share");
    }
  };

  // Widget Delete
  const handleDeleteWidget = (wid: number, name: string) => {
    setDeleteTarget({ type: "widget", id: wid, name });
  };

  // Save Dashboard Layout Data
  const saveLayouts = async (layoutsMap: Record<number, { x: number; y: number; w: number; h: number }>) => {
    if (!dashboard || !canUpdate) return;
    try {
      const payload = Object.entries(layoutsMap).map(([wid, l]) => ({
        widget_id: parseInt(wid),
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
      }));
      await baseapi.put(`/cdash/dashboards/${dashboard.id}/layout`, payload);
    } catch {
      // Best-effort
    }
  };

  const gridLayouts = useMemo(() => {
    if (!dashboard?.widgets) return { lg: [] };
    return {
      lg: dashboard.widgets.map((w: any, idx: number) => {
        const l = widgetLayouts[w.id] || w.layout || {};
        return {
          i: String(w.id),
          x: l.x ?? (idx % 2) * 6,
          y: l.y ?? Math.floor(idx / 2) * 4,
          w: l.w ?? 6,
          h: l.h ?? 4,
          minW: 2,
          minH: 2,
        };
      }),
    };
  }, [dashboard?.widgets, widgetLayouts]);

  const handleGridChange = (_currentLayout: any, allLayouts: any) => {
    if (!dashboard || readOnly || !dashboard.can_edit || !canUpdate) return;
    const items = allLayouts?.lg || _currentLayout || [];
    if (!Array.isArray(items)) return;

    const nextLayouts: Record<number, { x: number; y: number; w: number; h: number }> = {};
    items.forEach((item: any) => {
      const wid = Number(item.i);
      if (!isNaN(wid)) {
        nextLayouts[wid] = {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        };
      }
    });

    setWidgetLayouts(nextLayouts);
    saveLayouts(nextLayouts);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalDashboards / itemsPerPage));
  }, [totalDashboards, itemsPerPage]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
        <LayoutDashboard className="w-10 h-10 mb-4 opacity-50 text-gray-400 dark:text-gray-600" />
        <p className="text-sm font-medium">You do not have permission to view dashboards.</p>
      </div>
    );
  }

  return (
    <div className={`custom-dash-v2 w-full h-full overflow-y-auto overflow-x-hidden custom-scroll bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 flex flex-col font-sans ${readOnly ? "p-0" : "p-2 pb-24"}`}>
      {/* Scope Container for isolation styling */}
      <div className="w-full flex-1 flex flex-col">
        {/* Main Header / Topbar */}
        {!readOnly && (
          <div className="shrink-0 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-lg shrink-0">
                <LayoutDashboard size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
                  Custom Dashboards
                  {/* <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-orange-600/20 text-orange-400 border border-orange-500/20">V2</span> */}
                </h1>
                {/* <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                  Dynamic SQL Aggregations Engine
                </p> */}
              </div>
            </div>
          </div>
        )}

        {/* ─── View: Dashboards List (Rendered as Table exactly like V1) ─── */}
        {view === "list" && (
          <div className="flex flex-col flex-1">
            <div className="flex items-center justify-end gap-3 mb-3">
              <button
                onClick={() => {
                  setSearchInput("");
                  setSearchTerm("");
                  fetchDashboards();
                }}
                disabled={loadingList}
                className="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50 shrink-0 shadow-sm"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin text-orange-500 dark:text-orange-400" : ""}`} />
              </button>
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search dashboards..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9 pr-3 py-1.5 rounded-md bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-200 border border-gray-200 dark:border-gray-600 focus:outline-none focus:border-orange-500 w-64 transition-colors placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
                />
              </form>
              <ColumnVisibilityDropdown
                columns={DASHBOARD_COLUMN_OPTIONS}
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
              />
              {canCreate && (
                <button
                  onClick={() => {
                    setCreateName("");
                    setCreateDesc("");
                    setIsCreateModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg transition-colors border border-orange-500 shrink-0 shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" /> Create
                </button>
              )}
            </div>

            {/* Table wrapper matching V1 exactly */}
            <div className="bg-white dark:bg-gray-900/40 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col relative shadow-sm">
              <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                <table className="w-full table-fixed text-left min-w-[700px]">
                  <colgroup>
                    {visibleColumns.name && <col className="w-[30%]" />}
                    {visibleColumns.description && <col className="w-[42%]" />}
                    {visibleColumns.widgets && <col className="w-[14%]" />}
                    {visibleColumns.actions && <col className="w-[14%]" />}
                  </colgroup>
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      {visibleColumns.name && (
                        <th className="px-3 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-1.5">

                            <span>Name</span>
                          </div>
                        </th>
                      )}
                      {visibleColumns.description && (
                        <th className="px-3 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-1.5">

                            <span>Description</span>
                          </div>
                        </th>
                      )}
                      {visibleColumns.widgets && (
                        <th className="px-3 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-1.5">

                            <span>Widgets</span>
                          </div>
                        </th>
                      )}
                      {visibleColumns.actions && (
                        <th className="px-3 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider text-center">
                          <div className="flex items-center justify-center gap-1.5">

                            <span>Actions</span>
                          </div>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/50">
                    {loadingList ? (
                      <tr>
                        <td colSpan={visibleColumnCount || 1} className="h-32">
                          <div className="flex flex-col items-center justify-center space-y-3 h-full">
                            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
                          </div>
                        </td>
                      </tr>
                    ) : dashboards.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumnCount || 1} className="h-32">
                          <div className="flex flex-col items-center justify-center space-y-2 opacity-50 h-full">
                            <LayoutDashboard size={32} className="text-gray-400 dark:text-gray-200 mb-2" />
                            <p className="text-[11px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">
                              No dashboards found
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      dashboards.map((d) => (
                        <tr key={d.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors duration-100">
                          {/* Name */}
                          {visibleColumns.name && (
                            <td className="px-3 py-2">
                              <button
                                onClick={() => {
                                  setDashboardId(d.id);
                                  setView("dashboard");
                                }}
                                className="text-[12px] font-bold text-gray-900 dark:text-white hover:text-orange-500 dark:hover:text-orange-400 truncate text-left transition-colors"
                                title={d.name}
                              >
                                {d.name}
                              </button>
                            </td>
                          )}

                          {/* Description */}
                          {visibleColumns.description && (
                            <td className="px-3 py-2">
                              <span className="text-[11px] text-gray-600 dark:text-gray-300 truncate block" title={d.description}>
                                {d.description || "—"}
                              </span>
                            </td>
                          )}

                          {/* Widgets Count */}
                          {visibleColumns.widgets && (
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                                <Activity size={9} />
                                {d.widget_count} widget{d.widget_count === 1 ? "" : "s"}
                              </span>
                            </td>
                          )}

                          {/* Actions */}
                          {visibleColumns.actions && (
                            <td className="px-3 py-1 text-right truncate">
                              <div className="flex items-center justify-center">
                                <button
                                  data-menu-id={d.id}
                                  onClick={(e) => handleMenuOpen(e, d.id)}
                                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                                  title="Actions"
                                >
                                  <CircleEllipsis className="w-4.5 h-4.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                  {!loadingList && totalDashboards > 0 && (
                    <tfoot className="bg-gray-50/90 dark:bg-gray-900/60 sticky bottom-0 border-t border-gray-200 dark:border-gray-700">
                      <tr>
                        <td colSpan={visibleColumnCount || 1} className="p-0">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            itemsPerPage={itemsPerPage}
                            totalItems={totalDashboards}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            itemsPerPageOptions={[5, 10, 25, 50]}
                          />
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── View: Dashboard Canvas (Cards styled exactly like V1 builder) ─── */}
        {view === "dashboard" && (
          <div className="flex flex-col flex-1">
            {/* Canvas Sub-Header */}
            {!readOnly && (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => setView("list")}
                    className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg border border-gray-200 dark:border-gray-700 transition-colors shrink-0 shadow-sm"
                    title="Back to list"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      {dashboard?.name}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-455 mt-0.5">
                      {dashboard?.description || "Interactive dynamic dashboard canvas"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Time range select commented out for now
                  <select
                    value={rangeKey}
                    onChange={(e) => setRangeKey(e.target.value)}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-gray-200 px-3 py-1.5 focus:outline-none focus:border-orange-500"
                  >
                    {RANGES.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  */}

                  <button
                    onClick={refreshAll}
                    className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors shadow-sm"
                    title="Refresh widgets"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>

                  {dashboard?.can_edit && canUpdate && (
                    <button
                      onClick={openRenameModal}
                      className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                    >
                      Rename
                    </button>
                  )}

                  {dashboard?.can_edit && canUpdate && (
                    <button
                      onClick={() => setIsBuilderOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg transition-colors border border-orange-500 shadow-sm"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Widget
                    </button>
                  )}
                </div>
              </div>
            )}

            {loadingDashboard ? (
              <div className="flex-1 flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                <p className="text-xs text-gray-500 dark:text-gray-455 uppercase tracking-widest font-semibold">Loading widgets...</p>
              </div>
            ) : !dashboard?.widgets || dashboard.widgets.length === 0 ? (
              <div className="flex-1 border border-dashed border-gray-300 dark:border-gray-800 rounded-2xl p-16 text-center text-gray-500 dark:text-gray-400 bg-white/60 dark:bg-transparent flex flex-col items-center justify-center gap-4">
                <div className="p-3 bg-gray-100 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-inner">
                  <Grid className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm">No widgets yet</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-455 mt-1 max-w-sm">
                    Configure and align values, slices, groupings, and split columns to plot dynamic analytics graphs.
                  </p>
                </div>
                {dashboard?.can_edit && canUpdate && !readOnly && (
                  <button
                    onClick={() => setIsBuilderOpen(true)}
                    className="px-3.5 py-1.5 bg-orange-500/10 dark:bg-orange-600/15 hover:bg-orange-600 border border-orange-500/30 dark:border-orange-500/25 hover:border-orange-500 text-orange-600 dark:text-orange-400 hover:text-white text-xs font-semibold rounded-lg transition-all"
                  >
                    ＋ Add Widget
                  </button>
                )}
              </div>
            ) : (
              /* Grid Layout Canvas using react-grid-layout */
              <div className="w-full border border-gray-200 dark:border-gray-800 rounded-2xl bg-gray-100/40 dark:bg-gray-900/40 p-4 min-h-[450px]">
                <ResponsiveGridLayout
                  className="layout"
                  layouts={gridLayouts}
                  breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                  cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                  rowHeight={70}
                  margin={[12, 12]}
                  containerPadding={[0, 0]}
                  isDraggable={!readOnly && dashboard?.can_edit && canUpdate}
                  isResizable={!readOnly && dashboard?.can_edit && canUpdate}
                  resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
                  onLayoutChange={handleGridChange}
                  draggableHandle=".drag-handle"
                >
                  {dashboard?.widgets.map((w: any) => (
                    <div key={String(w.id)}>
                      <div className="h-full flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-xl overflow-hidden shadow-sm transition-colors group">
                        {/* Widget Header */}
                        <div
                          className={`drag-handle widget-head flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60 select-none shrink-0 ${!readOnly && dashboard?.can_edit && canUpdate ? "cursor-grab active:cursor-grabbing" : ""}`}
                        >
                          <div className="flex items-center gap-2 truncate max-w-[80%]">
                            <DynamicLucideIcon
                              name={w.visualization_config?.icon}
                              className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0"
                            />
                            <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate" title={w.name}>
                              {w.name}
                            </h4>
                          </div>
                          <div className="flex items-center gap-1">
                            {dashboard?.can_edit && !readOnly && (
                              <>
                                {canUpdate && (
                                  <button
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={() => {
                                      setEditingWidget(w);
                                      setIsBuilderOpen(true);
                                    }}
                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={() => handleDeleteWidget(w.id, w.name)}
                                    className="p-1 hover:bg-red-50 dark:hover:bg-red-500/25 text-gray-400 dark:text-gray-455 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Widget Body */}
                        <div className="widget-body flex-1 min-h-0 p-3 relative flex flex-col overflow-hidden">
                          {widgetLoading[w.id] && !widgetResults[w.id] ? (
                            <div className="flex-grow flex items-center justify-center">
                              <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                            </div>
                          ) : widgetErrors[w.id] ? (
                            <div className="flex-grow text-red-600 dark:text-red-400 text-xs p-2 overflow-auto font-mono leading-relaxed bg-red-50 dark:bg-red-950/10 border border-red-200 dark:border-red-950/20 rounded-lg">
                              {widgetErrors[w.id]}
                            </div>
                          ) : (
                            <div className="relative flex-grow flex flex-col min-h-0">
                              {widgetLoading[w.id] && (
                                <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 z-20 flex items-center justify-center backdrop-blur-[1px] rounded-lg">
                                  <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                                </div>
                              )}
                              <WidgetRendererV2
                                widget={w}
                                queryResult={widgetResults[w.id]}
                                onPageChange={(skip, limit) => loadWidgetData(dashboard!.id, w.id, skip, limit, w)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </ResponsiveGridLayout>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Widget Builder Wizard Modal */}
      {isBuilderOpen && (
        <WidgetBuilderModal
          dashboardId={dashboardId!}
          widget={editingWidget}
          existingLayouts={Object.values(widgetLayouts)}
          onClose={() => {
            setIsBuilderOpen(false);
            setEditingWidget(null);
          }}
          onSaved={() => {
            setIsBuilderOpen(false);
            setEditingWidget(null);
            fetchDashboardDetails(dashboardId!);
          }}
        />
      )}

      {/* ─── Create Dashboard Modal Overlay ─── */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <form onSubmit={handleCreateSubmit} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-md w-full shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/60">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Create Dashboard</h2>
              <button type="button" onClick={() => setIsCreateModalOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4 bg-white dark:bg-gray-900">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-400">Dashboard Name</label>
                <input
                  type="text"
                  required
                  placeholder="Enter name..."
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 rounded-lg px-3.5 py-2 w-full font-semibold"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">Description (Optional)</label>
                <textarea
                  placeholder="Enter description..."
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  rows={3}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-550 focus:outline-none focus:border-orange-500 rounded-lg px-3.5 py-2 w-full font-semibold resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/30">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg border border-orange-500 transition-colors shadow-lg shadow-orange-600/10 active:scale-[0.98]"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── Rename Dashboard Modal Overlay ─── */}
      {isRenameModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <form onSubmit={handleRenameSubmit} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-md w-full shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/60">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Rename Dashboard</h2>
              <button type="button" onClick={() => setIsRenameModalOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4 bg-white dark:bg-gray-900">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-440">New Name</label>
                <input
                  type="text"
                  required
                  placeholder="Enter name..."
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 rounded-lg px-3.5 py-2 w-full font-semibold"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-440">Description (Optional)</label>
                <textarea
                  placeholder="Enter description..."
                  value={renameDesc}
                  onChange={(e) => setRenameDesc(e.target.value)}
                  rows={3}
                  className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-550 focus:outline-none focus:border-orange-500 rounded-lg px-3.5 py-2 w-full font-semibold resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/30">
              <button
                type="button"
                onClick={() => setIsRenameModalOpen(false)}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg border border-orange-500 transition-colors shadow-lg shadow-orange-600/10 active:scale-[0.98]"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── Share Dashboard Modal Overlay ─── */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <form onSubmit={handleShareSubmit} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-sm w-full shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/60">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Share Dashboard</h2>
              <button type="button" onClick={() => setIsShareModalOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4 bg-white dark:bg-gray-900">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-400">User ID</label>
                <input
                  type="text"
                  required
                  placeholder="Enter target User ID..."
                  value={shareUserId}
                  onChange={(e) => setShareUserId(e.target.value)}
                  className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-550 focus:outline-none focus:border-orange-500 rounded-lg px-3.5 py-2 w-full font-semibold"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/30">
              <button
                type="button"
                onClick={() => setIsShareModalOpen(false)}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg border border-orange-500 transition-colors shadow-lg shadow-orange-600/10 active:scale-[0.98]"
              >
                Share
              </button>
            </div>
          </form>
        </div>
      )}
      {/* ─── Delete Confirmation Modal Overlay ─── */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-sm w-full shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/60">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Confirm Deletion</h2>
              <button type="button" onClick={() => setDeleteTarget(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-3 bg-white dark:bg-gray-900">
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                Are you sure you want to permanently delete the {deleteTarget.type} <span className="text-gray-900 dark:text-white font-bold">"{deleteTarget.name}"</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/30">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-650 hover:bg-red-600 text-white text-xs font-semibold rounded-lg border border-red-600 transition-colors shadow-lg shadow-red-600/10 active:scale-[0.98]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Portal Menu */}
      {openMenuId && createPortal(
        (() => {
          const activeDashboard = dashboards.find((d) => String(d.id) === String(openMenuId));
          if (!activeDashboard) return null;
          return (
            <div
              data-action-menu
              className="fixed w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5 animate-in fade-in zoom-in-95 duration-100"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            >
              <button
                onClick={() => {
                  setDashboardId(activeDashboard.id);
                  setView("dashboard");
                  setOpenMenuId(null);
                }}
                className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
              >
                <Eye className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> View Details
              </button>
              {canUpdate && (
                <button
                  onClick={() => {
                    openRenameModalFor(activeDashboard);
                    setOpenMenuId(null);
                  }}
                  className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                >
                  <Edit2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Edit Dashboard
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => {
                    handleDeleteDashboard(activeDashboard.id, activeDashboard.name);
                    setOpenMenuId(null);
                  }}
                  className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete Dashboard
                </button>
              )}
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── Widget Builder Modal Component ──────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function WidgetBuilderModal({
  dashboardId,
  widget,
  existingLayouts = [],
  onClose,
  onSaved,
}: {
  dashboardId: number;
  widget?: any;
  existingLayouts?: Array<{ x: number; y: number; w: number; h: number }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const level = useClientLevel();
  const [name, setName] = useState(widget?.name || "");
  const canUpdate = hasPermission("cdash", "update");
  const [chartType, setChartType] = useState(widget?.chart_type || "bar");
  const [sources, setSources] = useState<any[]>([]);
  const [caps, setCaps] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Visualization config state for Axis Labels
  const [xAxisLabel, setXAxisLabel] = useState(() => {
    let vis = widget?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch { }
    }
    return vis?.x_axis_label || "";
  });

  const [yAxisLabel, setYAxisLabel] = useState(() => {
    let vis = widget?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch { }
    }
    return vis?.y_axis_label || "";
  });

  const [gaugeLabel, setGaugeLabel] = useState(() => {
    let vis = widget?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch { }
    }
    return vis?.gauge_label || vis?.label || "";
  });

  // Selected Lucide Icon state
  const [selectedIcon, setSelectedIcon] = useState<string>(() => {
    let vis = widget?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch { }
    }
    return vis?.icon || "";
  });

  // Table mode state: "raw" (Detail Table) vs "aggregated" (Grouped Table)
  const [tableMode, setTableMode] = useState<"raw" | "aggregated">(() => {
    const initialChartType = widget?.chart_type || "bar";
    if (initialChartType === "table") {
      let parsed: any = widget?.query_definition;
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          // fallback
        }
      }
      if (parsed) {
        if (Array.isArray(parsed.columns) && parsed.columns.length > 0) return "raw";
        if (Array.isArray(parsed.metrics) && parsed.metrics.length === 0) return "raw";
        if (parsed.x_axis || (Array.isArray(parsed.metrics) && parsed.metrics.length > 0)) return "aggregated";
      }
    }
    return "raw";
  });

  // Widget query definition state
  const [queryDef, setQueryDef] = useState<any>(() => {
    const defaults = {
      source: "",
      joins: [],
      metrics: [{ type: "count", alias: "count" }],
      x_axis: null,
      split_series: null,
      columns: [],
      filters: [],
      limit: 100,
    };
    if (widget?.query_definition) {
      let parsed: any = {};
      if (typeof widget.query_definition === "string") {
        try {
          parsed = JSON.parse(widget.query_definition);
        } catch {
          // fallback
        }
      } else {
        parsed = JSON.parse(JSON.stringify(widget.query_definition));
      }
      return {
        ...defaults,
        ...parsed,
        joins: parsed?.joins || [],
        metrics: parsed?.metrics || [{ type: "count", alias: "count" }],
        columns: parsed?.columns || [],
        filters: parsed?.filters || [],
      };
    }
    return defaults;
  });

  const [relatedSources, setRelatedSources] = useState<any[]>([]);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [debugSql, setDebugSql] = useState(false);
  const [showSqlPane, setShowSqlPane] = useState(false);
  const [savingWidget, setSavingWidget] = useState(false);

  // Load API Capabilities & Datasources list
  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const [srcRes, capsRes] = await Promise.all([
        baseapi.get("/cdash/meta/sources"),
        baseapi.get("/cdash/meta/capabilities"),
      ]);
      setSources(srcRes.data);
      setCaps(capsRes.data);

      // Default main source if not set
      if (!queryDef.source && srcRes.data.length > 0) {
        setQueryDef((prev: any) => ({ ...prev, source: srcRes.data[0].source }));
      }
    } catch (e: any) {
      showError(e.message || "Failed to load builder configurations");
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // Load related tables/joins when source shifts
  const loadRelated = async () => {
    if (!queryDef.source) return;
    try {
      const res = await baseapi.get(`/cdash/meta/sources/${queryDef.source}/related`);
      setRelatedSources(res.data);
    } catch {
      setRelatedSources([]);
    }
  };

  useEffect(() => {
    loadRelated();
  }, [queryDef.source]);

  // Combined picker list: main source + joins
  const pickerSourcesList = useMemo(() => {
    const prim = sources.find((s) => s.source === queryDef.source);
    const list = [{ source: queryDef.source, label: (prim?.label || queryDef.source) + " (primary)", prefix: "" }];
    (queryDef.joins || []).forEach((j: any) => {
      const meta = sources.find((s) => s.source === j.source);
      list.push({ source: j.source, label: meta?.label || j.source, prefix: j.source + "." });
    });
    return list;
  }, [sources, queryDef.source, queryDef.joins]);

  const activeCap = () => {
    if (!caps) return { metrics: [0, 10], buckets: [0, 2], split_series: false };
    return caps.chart_types[chartType] || { metrics: [0, 10], buckets: [0, 2], split_series: false };
  };

  // Build clean query def payload to send to query execute API
  const buildQueryPayload = () => {
    const out = JSON.parse(JSON.stringify(queryDef));
    if (chartType === "table" && tableMode === "raw") {
      out.metrics = [];
      out.x_axis = null;
      out.split_series = null;
      out.columns = Array.isArray(out.columns) ? out.columns : [];
    } else {
      out.columns = [];
      out.metrics = (out.metrics || []).filter((m: any) => m.type === "count" || m.field);
      if (out.x_axis && !out.x_axis.field) out.x_axis = null;
      if (out.split_series && !out.split_series.field) out.split_series = null;
      if (["metric", "gauge"].includes(chartType)) {
        out.x_axis = null;
        out.split_series = null;
        out.metrics = out.metrics.slice(0, 1);
      }
      if (["pie", "donut"].includes(chartType)) {
        out.split_series = null;
        out.metrics = out.metrics.slice(0, 1);
      }
      if (chartType === "heatmap") out.metrics = out.metrics.slice(0, 1);
      if (out.x_axis?.type === "date_histogram" || out.split_series) out.limit = Math.max(out.limit || 100, 500);
    }
    out.filters = (out.filters || []).filter(
      (f: any) => f.field && (f.value !== "" || ["exists", "not_exists"].includes(f.operator))
    );
    return out;
  };

  // Preview query definition execution
  const executePreview = async (skip?: number, limit?: number) => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const query = buildQueryPayload();
      const params: any = { debug: debugSql };

      if (chartType === "table") {
        const s = skip ?? 0;
        const l = limit ?? 10;
        query.skip = s;
        query.limit = l;
        params.skip = s;
        params.limit = l;
      }

      if (level && level.name !== "global" && level.id) {
        params.client_id = level.id;
        query.client_id = level.id;
      }
      const res = await baseapi.post("/cdash/query", query, {
        params,
      });
      setPreviewResult(res.data);
    } catch (e: any) {
      setPreviewError(e.response?.data?.detail ? JSON.stringify(e.response.data.detail) : e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-run preview when editing an existing widget
  useEffect(() => {
    if (widget) {
      executePreview();
    }
  }, [widget]);

  // Helper to find the first available layout spot for a new widget (w=6, h=4)
  const findFreeSpace = (
    existing: Array<{ x: number; y: number; w: number; h: number }>,
    w: number,
    h: number
  ) => {
    let y = 0;
    while (true) {
      for (let x = 0; x <= 12 - w; x++) {
        let overlaps = false;
        for (const layout of existing) {
          if (!layout) continue;
          const lx = layout.x ?? 0;
          const ly = layout.y ?? 0;
          const lw = layout.w ?? 6;
          const lh = layout.h ?? 4;
          if (
            x < lx + lw &&
            x + w > lx &&
            y < ly + lh &&
            y + h > ly
          ) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          return { x, y };
        }
      }
      y++; // check next row
    }
  };

  // Save Widget details
  const handleSaveWidget = async () => {
    if (!canUpdate) {
      showError("You do not have permission to save/update widgets.");
      return;
    }
    setSavingWidget(true);
    try {
      const widgetName = name.trim();
      if (!widgetName) {
        showError("Widget name is required");
        return;
      }
      let existingVis: Record<string, any> = {};
      if (widget?.visualization_config) {
        if (typeof widget.visualization_config === "string") {
          try {
            existingVis = JSON.parse(widget.visualization_config);
          } catch { }
        } else if (typeof widget.visualization_config === "object") {
          existingVis = widget.visualization_config;
        }
      }

      const isAxisChart = !["metric", "gauge", "pie", "donut", "table"].includes(chartType);
      const visConfig: Record<string, any> = { ...existingVis };
      if (selectedIcon.trim()) {
        visConfig.icon = selectedIcon.trim();
      } else {
        delete visConfig.icon;
      }
      if (isAxisChart && xAxisLabel.trim()) {
        visConfig.x_axis_label = xAxisLabel.trim();
      } else {
        delete visConfig.x_axis_label;
      }
      if (isAxisChart && yAxisLabel.trim()) {
        visConfig.y_axis_label = yAxisLabel.trim();
      } else {
        delete visConfig.y_axis_label;
      }
      if (chartType === "gauge" && gaugeLabel.trim()) {
        visConfig.gauge_label = gaugeLabel.trim();
        visConfig.label = gaugeLabel.trim();
      } else if (chartType === "gauge") {
        delete visConfig.gauge_label;
        delete visConfig.label;
      }

      const body = {
        name: widgetName,
        chart_type: chartType,
        query_definition: buildQueryPayload(),
        visualization_config: visConfig,
      };

      if (widget) {
        await baseapi.put(`/cdash/dashboards/${dashboardId}/widgets/${widget.id}`, body);
      } else {
        const freeSpace = findFreeSpace(existingLayouts, 6, 4);
        await baseapi.post(
          `/cdash/dashboards/${dashboardId}/widgets`,
          { ...body, layout: { x: freeSpace.x, y: freeSpace.y, w: 6, h: 4 } }
        );
      }
      showSuccess("Widget saved successfully");
      onSaved();
    } catch (e: any) {
      showError(e.message || "Failed to save widget");
    } finally {
      setSavingWidget(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-fade-in text-gray-900 dark:text-gray-100">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/60">
          <div className="flex items-center gap-4">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">{widget ? "Edit Widget" : "New Widget"}</h2>
            <input
              type="text"
              placeholder="Widget name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 w-56 font-semibold placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
            />
            <IconPickerPopover value={selectedIcon} onChange={setSelectedIcon} />
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {loadingConfig ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-xs text-gray-500 dark:text-gray-455 uppercase tracking-widest font-semibold">Configuring environment...</p>
          </div>
        ) : !caps ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 gap-2 text-red-500 dark:text-red-400 text-xs">
            Failed to load dynamic capabilities. Please check server connection.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto custom-scroll p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 bg-gray-50/60 dark:bg-gray-900">
            {/* Left Options Panel */}
            <div className="lg:col-span-5 flex flex-col gap-4 overflow-y-auto custom-scroll pr-1">
              {/* Step 1: Datasource */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">1 · Data Source</h3>
                <CustomSelect
                  value={queryDef.source}
                  options={sources.map((s) => ({ value: s.source, label: s.label }))}
                  onChange={(val) => {
                    setQueryDef((prev: any) => ({
                      ...prev,
                      source: val,
                      joins: [],
                      metrics: [{ type: "count", alias: "count" }],
                      x_axis: null,
                      split_series: null,
                      filters: [],
                    }));
                    setPreviewResult(null);
                    setPreviewError("");
                  }}
                />

                {/* Joins row */}
                {(queryDef.joins || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(queryDef.joins || []).map((j: any, i: number) => {
                      const meta = sources.find((s) => s.source === j.source);
                      return (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-[10px] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                        >
                          🔗 {meta?.label || j.source}
                          <button
                            onClick={() => {
                              const updatedJoins = [...(queryDef.joins || [])];
                              updatedJoins.splice(i, 1);
                              setQueryDef((prev: any) => ({ ...prev, joins: updatedJoins }));
                            }}
                            className="text-gray-400 dark:text-gray-550 hover:text-red-500 dark:hover:text-red-400"
                          >
                            ✕
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Add join selector */}
                {relatedSources.length > 0 && (queryDef.joins || []).length < 3 && (
                  <div className="mt-1">
                    <CustomSelect
                      value=""
                      placeholder="＋ Add related table..."
                      options={relatedSources
                        .filter((r) => !(queryDef.joins || []).some((j: any) => j.source === r.source))
                        .map((r) => ({
                          value: `${r.source}|${r.join_field}`,
                          label: `${r.label}${r.cardinality === "one_to_many" ? " ⚠ (Many)" : ""}`,
                        }))}
                      onChange={(val) => {
                        if (!val) return;
                        const [src, jf] = val.split("|");
                        setQueryDef((prev: any) => ({
                          ...prev,
                          joins: [...prev.joins, { source: src, join_field: jf }],
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Step 2: Chart Type */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">2 · Chart Type</h3>
                <div className="grid grid-cols-5 gap-2">
                  {Object.keys(caps?.chart_types || {}).map((ct) => {
                    const isSel = ct === chartType;
                    const border = isSel
                      ? "border-orange-500 bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold"
                      : "border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-700 hover:text-gray-900 dark:hover:text-white";
                    const IconComponent = CHART_ICONS[ct] || Info;
                    const iconColor = CHART_COLORS[ct] || "text-gray-400";
                    return (
                      <button
                        key={ct}
                        onClick={() => setChartType(ct)}
                        className={`flex flex-col items-center justify-center py-2.5 rounded-lg border text-[9px] uppercase tracking-wider text-center transition-all ${border}`}
                      >
                        <IconComponent className={`w-4 h-4 mb-1 ${iconColor}`} />
                        {ct.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Axis Labels (Visualization Config) for applicable graphs */}
              {!["metric", "gauge", "pie", "donut", "table"].includes(chartType) && (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">
                    Axis Labels
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] text-gray-600 dark:text-gray-400 font-semibold uppercase tracking-wider">
                        X-Axis Label
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Severity"
                        value={xAxisLabel}
                        onChange={(e) => setXAxisLabel(e.target.value)}
                        className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] text-gray-600 dark:text-gray-400 font-semibold uppercase tracking-wider">
                        Y-Axis Label
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Number of Vulns"
                        value={yAxisLabel}
                        onChange={(e) => setYAxisLabel(e.target.value)}
                        className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Gauge Label Config */}
              {chartType === "gauge" && (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">
                    Gauge Config
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] text-gray-600 dark:text-gray-400 font-semibold uppercase tracking-wider">
                      Gauge Label / Subtitle
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Total Vulnerabilities, Active Assets..."
                      value={gaugeLabel}
                      onChange={(e) => setGaugeLabel(e.target.value)}
                      className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>
                </div>
              )}

              {/* Mode Selector for Table Widgets */}
              {chartType === "table" && (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">Table Display Mode</h3>
                  <div className="grid grid-cols-2 gap-2 bg-gray-100 dark:bg-gray-900 p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                    <button
                      type="button"
                      onClick={() => setTableMode("raw")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${tableMode === "raw"
                          ? "bg-white dark:bg-orange-500/20 border border-gray-200 dark:border-orange-500/60 text-orange-600 dark:text-orange-400 shadow-sm"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-800/50"
                        }`}
                    >
                      <Table className="w-3.5 h-3.5" />
                      Raw Records (Detail Table)
                    </button>
                    <button
                      type="button"
                      onClick={() => setTableMode("aggregated")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${tableMode === "aggregated"
                          ? "bg-white dark:bg-orange-500/20 border border-gray-200 dark:border-orange-500/60 text-orange-600 dark:text-orange-400 shadow-sm"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-800/50"
                        }`}
                    >
                      <BarChart2 className="w-3.5 h-3.5" />
                      Aggregated Summary (Grouped Table)
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 & Beyond: Raw Records Column Selection vs Aggregation Config */}
              {chartType === "table" && tableMode === "raw" ? (
                <MultiColumnSelector
                  getSourcesList={() => pickerSourcesList}
                  selectedColumns={queryDef.columns || []}
                  onChange={(cols) => setQueryDef((prev: any) => ({ ...prev, columns: cols }))}
                />
              ) : (
                <>
                  {/* Step 3: Metrics */}
                  {activeCap().metrics[1] > 0 && (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">3 · Value (Measure)</h3>
                      {queryDef.metrics.map((m: any, idx: number) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <div className="w-[38%]">
                            <CustomSelect
                              value={m.type}
                              options={caps.aggregations.map((agg: string) => ({
                                value: agg,
                                label: AGG_LABELS[agg] || agg,
                              }))}
                              onChange={(val) => {
                                const updated = [...queryDef.metrics];
                                updated[idx] = { ...updated[idx], type: val };
                                setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                              }}
                            />
                          </div>

                          {m.type !== "count" && (
                            <div className="flex-1">
                              <FieldPickerComponent
                                getSourcesList={() => pickerSourcesList}
                                value={m.field}
                                onPick={(f) => {
                                  const updated = [...queryDef.metrics];
                                  updated[idx] = { ...updated[idx], field: f };
                                  setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                                }}
                                roleFilter={
                                  ["sum", "avg", "median", "percentiles", "std_deviation"].includes(m.type)
                                    ? (fl) => ["integer", "float"].includes(fl.type)
                                    : null
                                }
                              />
                            </div>
                          )}

                          {queryDef.metrics.length > Math.max(1, activeCap().metrics[0]) && (
                            <button
                              onClick={() => {
                                const updated = [...queryDef.metrics];
                                updated.splice(idx, 1);
                                setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                              }}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-855 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      {queryDef.metrics.length < activeCap().metrics[1] && (
                        <button
                          onClick={() =>
                            setQueryDef((prev: any) => ({
                              ...prev,
                              metrics: [...prev.metrics, { type: "count", alias: `m_${prev.metrics.length}` }],
                            }))
                          }
                          className="text-left text-xs text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-bold mt-1"
                        >
                          ＋ Add another value measure
                        </button>
                      )}
                    </div>
                  )}

                  {/* Step 4: Group By (buckets) */}
                  {activeCap().buckets[1] > 0 && (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 relative z-20 shadow-sm">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">
                        4 · Group By {["pie", "donut"].includes(chartType) ? "(Slices)" : "(X-Axis)"}
                      </h3>
                      <div className="flex gap-2">
                        <div className="w-[38%]">
                          <CustomSelect
                            value={queryDef.x_axis?.type || ""}
                            options={[
                              { value: "", label: "— none —" },
                              ...caps.bucket_types
                                .filter((b: string) => b !== "filters")
                                .map((b: string) => ({
                                  value: b,
                                  label: b.replace(/_/g, " "),
                                })),
                            ]}
                            onChange={(val) => {
                              if (!val) {
                                setQueryDef((prev: any) => ({ ...prev, x_axis: null }));
                              } else {
                                setQueryDef((prev: any) => ({
                                  ...prev,
                                  x_axis: { type: val, field: "", limit: 10, interval: "day" },
                                }));
                              }
                            }}
                          />
                        </div>

                        {queryDef.x_axis && (
                          <div className="flex-1">
                            <FieldPickerComponent
                              getSourcesList={() => pickerSourcesList}
                              value={queryDef.x_axis.field}
                              onPick={(f) =>
                                setQueryDef((prev: any) => ({
                                  ...prev,
                                  x_axis: { ...prev.x_axis, field: f },
                                }))
                              }
                              roleFilter={
                                queryDef.x_axis.type === "date_histogram"
                                  ? (fl) => ["datetime", "date"].includes(fl.type)
                                  : queryDef.x_axis.type === "histogram" || queryDef.x_axis.type === "range"
                                    ? (fl) => ["integer", "float"].includes(fl.type)
                                    : null
                              }
                            />
                          </div>
                        )}
                      </div>

                      {queryDef.x_axis?.type === "date_histogram" && (
                        <div className="flex justify-between items-center gap-4 mt-2">
                          <span className="text-[11px] text-gray-600 dark:text-gray-400">Date interval:</span>
                          <div className="w-36">
                            <CustomSelect
                              value={queryDef.x_axis.interval || "day"}
                              options={caps.time_intervals.map((t: string) => ({
                                value: t,
                                label: t,
                              }))}
                              onChange={(val) =>
                                setQueryDef((prev: any) => ({
                                  ...prev,
                                  x_axis: { ...prev.x_axis, interval: val },
                                }))
                              }
                            />
                          </div>
                        </div>
                      )}

                      {queryDef.x_axis?.type === "terms" && (
                        <div className="flex justify-between items-center gap-4 mt-2">
                          <span className="text-[11px] text-gray-600 dark:text-gray-400">Limit top:</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={queryDef.x_axis.limit || 10}
                            onChange={(e) =>
                              setQueryDef((prev: any) => ({
                                ...prev,
                                x_axis: { ...prev.x_axis, limit: parseInt(e.target.value) || 10 },
                              }))
                            }
                            className="w-20 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-2.5 py-1 text-center font-mono focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 5: Split by */}
                  {activeCap().split_series && queryDef.x_axis && (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">5 · Split Series</h3>
                      <div className="flex gap-2 items-center">
                        <div className="flex-1">
                          <FieldPickerComponent
                            getSourcesList={() => pickerSourcesList}
                            value={queryDef.split_series?.field}
                            placeholder="— choose column to split —"
                            onPick={(f) =>
                              setQueryDef((prev: any) => ({
                                ...prev,
                                split_series: { type: "terms", field: f, limit: 8 },
                              }))
                            }
                            roleFilter={(fl) => fl.role !== "time"}
                          />
                        </div>
                        {queryDef.split_series && (
                          <button
                            onClick={() => setQueryDef((prev: any) => ({ ...prev, split_series: null }))}
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-855 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Step 6: Filters */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 relative z-0 shadow-sm">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450">Filters</h3>
                {queryDef.filters.map((f: any, idx: number) => (
                  <div
                    key={idx}
                    style={{ zIndex: queryDef.filters.length - idx }}
                    className="flex flex-col gap-2 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 p-2.5 rounded-lg relative"
                  >
                    <button
                      onClick={() => {
                        const updated = [...queryDef.filters];
                        updated.splice(idx, 1);
                        setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                      }}
                      className="absolute top-2 right-2 p-1 hover:bg-gray-200/60 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 rounded"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>

                    <div className="flex gap-2 pr-6 relative z-20">
                      <div className="flex-1">
                        <FieldPickerComponent
                          getSourcesList={() => pickerSourcesList}
                          value={f.field}
                          onPick={(v) => {
                            const updated = [...queryDef.filters];
                            updated[idx] = { ...updated[idx], field: v, value: "" };
                            setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                          }}
                        />
                      </div>
                      <div className="w-[44%]">
                        <CustomSelect
                          value={f.operator}
                          options={caps.filter_operators.map((op: string) => ({
                            value: op,
                            label: OP_LABELS[op] || op,
                          }))}
                          onChange={(val) => {
                            const updated = [...queryDef.filters];
                            updated[idx] = { ...updated[idx], operator: val };
                            setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                          }}
                        />
                      </div>
                    </div>

                    {!["exists", "not_exists"].includes(f.operator) && (
                      (() => {
                        const { source: filterSource, field: filterField } = getFilterSourceAndField(f.field, queryDef);
                        return (
                          <div className="relative z-10">
                            <FilterValueInput
                              source={filterSource}
                              field={filterField}
                              operator={f.operator}
                              value={f.value}
                              onChange={(val) => {
                                const updated = [...queryDef.filters];
                                updated[idx] = { ...updated[idx], value: val };
                                setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                              }}
                            />
                          </div>
                        );
                      })()
                    )}
                  </div>
                ))}
                <button
                  onClick={() =>
                    setQueryDef((prev: any) => ({
                      ...prev,
                      filters: [...prev.filters, { field: "", operator: "eq", value: "" }],
                    }))
                  }
                  className="text-left text-xs text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-bold mt-1"
                >
                  ＋ Add Query Filter
                </button>
              </div>
            </div>

            {/* Right Preview Panel */}
            <div className="lg:col-span-7 flex flex-col gap-4 min-h-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Live Preview</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setDebugSql(!debugSql);
                      if (!debugSql) {
                        setShowSqlPane(true);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-all ${debugSql ? "bg-orange-500/10 dark:bg-orange-600/15 border-orange-500 text-orange-600 dark:text-orange-400" : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                  >
                    <Code className="w-3.5 h-3.5" /> Debug SQL
                  </button>
                  <button
                    onClick={() => executePreview()}
                    disabled={previewLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-600 text-[11px] font-semibold rounded-lg transition-colors shadow-sm"
                  >
                    {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500 dark:text-orange-400" /> : <Eye className="w-3.5 h-3.5" />} Preview
                  </button>
                </div>
              </div>

              {/* Chart Rendering Preview Box */}
              <div className="flex-1 min-h-[300px] border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 rounded-xl p-4 flex flex-col justify-center relative overflow-hidden">
                {previewLoading && !previewResult ? (
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-7 h-7 text-orange-500 animate-spin" />
                    <p className="text-[11px] text-gray-500 uppercase tracking-widest font-semibold">Executing SQL query...</p>
                  </div>
                ) : previewError ? (
                  <div className="text-red-600 dark:text-red-400 text-xs p-3 overflow-auto font-mono leading-relaxed bg-red-50 dark:bg-red-950/10 border border-red-200 dark:border-red-950/20 rounded-lg">
                    {previewError}
                  </div>
                ) : previewResult ? (
                  <div className="relative flex-1 flex flex-col min-h-0">
                    {previewLoading && (
                      <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 z-20 flex flex-col items-center justify-center gap-3 backdrop-blur-[1px] rounded-xl">
                        <Loader2 className="w-7 h-7 text-orange-500 animate-spin" />
                        <p className="text-[11px] text-gray-500 uppercase tracking-widest font-semibold">Executing SQL query...</p>
                      </div>
                    )}
                    <WidgetRendererV2
                      widget={{
                        chart_type: chartType,
                        query_definition: buildQueryPayload(),
                        visualization_config: {
                          ...(typeof widget?.visualization_config === "object" ? widget.visualization_config : {}),
                          ...(selectedIcon.trim() ? { icon: selectedIcon.trim() } : {}),
                          ...(chartType === "gauge" && gaugeLabel.trim() ? { gauge_label: gaugeLabel.trim(), label: gaugeLabel.trim() } : {}),
                          ...(!["metric", "gauge", "pie", "donut", "table"].includes(chartType)
                            ? {
                              ...(xAxisLabel.trim() ? { x_axis_label: xAxisLabel.trim() } : {}),
                              ...(yAxisLabel.trim() ? { y_axis_label: yAxisLabel.trim() } : {}),
                            }
                            : {}),
                        },
                      }}
                      queryResult={previewResult}
                      onPageChange={(skip, limit) => executePreview(skip, limit)}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-500 text-xs gap-1 opacity-70">
                    <Info className="w-5 h-5 text-gray-400 dark:text-gray-500 mb-1" />
                    <p>No preview generated.</p>
                    <p>Adjust data parameters and click "Preview" above.</p>
                  </div>
                )}
              </div>

              {/* SQL Debug details */}
              {debugSql && showSqlPane && previewResult?.sql && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl p-3 flex flex-col gap-2 shrink-0">
                  <div className="flex justify-between items-center text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-800 pb-1.5">
                    <span>Generated SQL Command</span>
                    <button onClick={() => setShowSqlPane(false)} className="text-gray-400 dark:text-gray-550 hover:text-gray-700 dark:hover:text-white">
                      ✕
                    </button>
                  </div>
                  <pre className="text-[10px] text-orange-600 dark:text-orange-400/90 font-mono overflow-auto max-h-[120px] select-all leading-normal whitespace-pre-wrap">
                    {previewResult.sql}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50/80 dark:bg-gray-800/30">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold rounded-xl transition-colors shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveWidget}
            disabled={savingWidget || loadingConfig}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg border border-orange-500 transition-colors shadow-lg shadow-orange-600/10 active:scale-[0.98]"
          >
            {savingWidget ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {widget ? "Save Widget" : "Add to Dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── Field Picker Component (Handles drill-downs inside JSONB) ───────────────
// ─────────────────────────────────────────────────────────────────────────────

interface FieldPickerDrill {
  source: string;
  prefix: string;
  baseField: string;
  path: string;
  isArray: boolean;
  parent: FieldPickerDrill | null;
}

function FieldPickerComponent({
  getSourcesList,
  value,
  onPick,
  placeholder = "Select field column...",
  roleFilter = null,
}: {
  getSourcesList: () => any[];
  value?: string;
  onPick: (v: string) => void;
  placeholder?: string;
  roleFilter?: ((f: any) => boolean) | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [drill, setDrill] = useState<FieldPickerDrill | null>(null);

  // JSON keys cache
  const [jsonKeys, setJsonKeys] = useState<string[]>([]);
  const [jsonIsArray, setJsonIsArray] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // Grouped Fields Lists
  const [sourceFields, setSourceFields] = useState<Record<string, any[]>>({});
  const [loadingFields, setLoadingFields] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setDrill(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch fields for active sources when dropdown opens
  const fetchSourceFields = async () => {
    setLoadingFields(true);
    try {
      const entries = getSourcesList();
      const fieldsMap: Record<string, any[]> = {};
      await Promise.all(
        entries.map(async (src) => {
          try {
            const res = await baseapi.get(`/cdash/meta/sources/${src.source}/fields`);
            fieldsMap[src.source] = res.data;
          } catch {
            fieldsMap[src.source] = [];
          }
        })
      );
      setSourceFields(fieldsMap);
    } catch {
      // ignore
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    if (isOpen && !drill) {
      fetchSourceFields();
    }
  }, [isOpen, drill]);

  // Fetch JSON keys when drill context is updated
  useEffect(() => {
    if (!drill) return;
    const fetchKeys = async () => {
      setLoadingKeys(true);
      try {
        const res = await baseapi.get(`/cdash/meta/sources/${drill.source}/jsonb/${drill.baseField}`, {
          params: { path: drill.path },
        });
        setJsonKeys(res.data?.keys || []);
        setJsonIsArray(!!res.data?.is_array);
      } catch {
        setJsonKeys([]);
        setJsonIsArray(false);
      } finally {
        setLoadingKeys(false);
      }
    };
    fetchKeys();
  }, [drill]);

  const handleSelect = (fullPath: string) => {
    onPick(fullPath);
    setIsOpen(false);
    setDrill(null);
  };

  return (
    <div className={`relative w-full ${isOpen ? "z-[100]" : "z-10"}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white dark:bg-gray-805 border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-600 text-left text-xs text-gray-900 dark:text-white px-3 py-2 rounded-lg font-semibold truncate flex items-center justify-between shadow-sm"
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0 rotate-90" />
      </button>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 min-w-[260px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] max-h-[300px] overflow-y-auto custom-scroll p-2 flex flex-col gap-1">
          {drill ? (
            /* Drill-down View (JSON path navigation) */
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setDrill(drill.parent)}
                className="w-full text-left text-[10px] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 flex items-center gap-1.5 font-bold"
              >
                ← Back
              </button>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider px-2 py-1 select-none font-bold">
                Drill: {drill.baseField}
                {drill.path ? `.${drill.path}` : ""}
                {jsonIsArray ? " [List]" : ""}
              </div>
              <button
                type="button"
                onClick={() => handleSelect(drill.prefix + drill.baseField + (drill.path ? `.${drill.path}` : ""))}
                className="w-full text-left text-xs text-orange-600 dark:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 px-2.5 py-1.5 rounded font-bold"
              >
                Use this JSON path
              </button>

              {loadingKeys ? (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                  <Loader2 className="w-3 h-3 animate-spin text-orange-500" /> Fetching keys...
                </div>
              ) : jsonKeys.length === 0 ? (
                <div className="text-[10px] text-gray-500 px-3 py-2 font-medium italic">No nested keys found in logs.</div>
              ) : (
                jsonKeys.map((key) => {
                  const segment = jsonIsArray ? `0.${key}` : key;
                  const nextPath = drill.path ? `${drill.path}.${segment}` : segment;

                  return (
                    <div key={key} className="flex items-center justify-between group hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                      <button
                        type="button"
                        onClick={() => handleSelect(drill.prefix + drill.baseField + `.${nextPath}`)}
                        className="flex-1 text-left text-xs text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded truncate"
                      >
                        {key}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDrill({
                            source: drill.source,
                            prefix: drill.prefix,
                            baseField: drill.baseField,
                            path: nextPath,
                            isArray: false,
                            parent: drill,
                          })
                        }
                        className="p-1 px-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-700 text-[11px]"
                      >
                        ›
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* Root list (Field columns by Datasource) */
            <div className="flex flex-col gap-1">
              {loadingFields ? (
                <div className="flex items-center justify-center p-6 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                  <span className="text-xs text-gray-500">Fetching schema columns...</span>
                </div>
              ) : (
                getSourcesList().map((src) => {
                  const fields = sourceFields[src.source] || [];
                  return (
                    <div key={src.source} className="flex flex-col gap-1">
                      <div className="text-[9px] uppercase tracking-wider text-orange-600 dark:text-orange-500 font-bold px-2.5 pt-2 pb-1 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 select-none">
                        {src.label}
                      </div>
                      {fields
                        .filter((f) => !roleFilter || f.is_jsonb || roleFilter(f))
                        .map((f) => (
                          <button
                            key={f.name}
                            type="button"
                            onClick={() => {
                              if (f.is_jsonb) {
                                setDrill({
                                  source: src.source,
                                  prefix: src.prefix,
                                  baseField: f.name,
                                  path: "",
                                  isArray: false,
                                  parent: null,
                                });
                              } else {
                                handleSelect(src.prefix + f.name);
                              }
                            }}
                            className="w-full text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1.5 rounded flex justify-between items-center font-medium"
                          >
                            <span className="truncate">{f.label || f.name}</span>
                            <span className="text-[9px] text-gray-500 select-none font-bold uppercase">
                              {f.is_jsonb ? "JSON ›" : f.type}
                            </span>
                          </button>
                        ))}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── Filter Value Input Component (Fetches /cdash/meta/sources/:source/fields/:field/values)
// ─────────────────────────────────────────────────────────────────────────────

function getFilterSourceAndField(fieldPath: string, queryDef: any) {
  if (!fieldPath) return { source: "", field: "" };
  let source = queryDef.source || "";
  let field = fieldPath;

  if (queryDef.joins?.length) {
    for (const j of queryDef.joins) {
      if (fieldPath.startsWith(j.source + ".")) {
        source = j.source;
        field = fieldPath.slice(j.source.length + 1);
        break;
      }
    }
  }
  return { source, field };
}

interface FilterValueInputProps {
  source: string;
  field: string;
  operator: string;
  value: any;
  onChange: (val: any) => void;
}

function FilterValueInput({ source, field, operator, value, onChange }: FilterValueInputProps) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch field values from /cdash/meta/sources/{source}/fields/{field}/values
  const fetchValues = async () => {
    if (!source || !field) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const storedLevel = localStorage.getItem("level");
      const levelObj = storedLevel ? JSON.parse(storedLevel) : null;
      const params: any = {};
      if (levelObj && levelObj.name !== "global" && levelObj.id) {
        params.client_id = levelObj.id;
      }

      const res = await baseapi.get(`/cdash/meta/sources/${source}/fields/${field}/values`, { params });
      let raw: any[] = [];
      if (Array.isArray(res.data)) {
        raw = res.data;
      } else if (res.data && Array.isArray(res.data.values)) {
        raw = res.data.values;
      } else if (res.data && Array.isArray(res.data.data)) {
        raw = res.data.data;
      }

      const formatted = raw.map((item: any) => {
        if (item && typeof item === "object") {
          const val = item.value ?? item.id ?? item.name ?? String(item);
          const lbl = item.label ?? item.name ?? item.title ?? String(val);
          return { value: String(val), label: String(lbl) };
        }
        return { value: String(item), label: String(item) };
      });
      setOptions(formatted);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && source && field) {
      fetchValues();
    }
  }, [isOpen, source, field]);

  const isMulti = ["in", "not_in", "between"].includes(operator);
  const displayVal = Array.isArray(value) ? value.join(", ") : value ?? "";

  const filteredOptions = useMemo(() => {
    if (!filterText.trim()) return options;
    const lower = filterText.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(lower) || o.value.toLowerCase().includes(lower)
    );
  }, [options, filterText]);

  const handleSelectOption = (optVal: string) => {
    if (isMulti) {
      const currentArray = Array.isArray(value)
        ? value
        : typeof value === "string" && value.trim()
          ? value.split(",").map((s) => s.trim()).filter(Boolean)
          : [];
      const nextArray = currentArray.includes(optVal)
        ? currentArray.filter((v: string) => v !== optVal)
        : [...currentArray, optVal];
      onChange(nextArray);
    } else {
      onChange(optVal);
      setIsOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setFilterText(text);
    if (isMulti) {
      const arr = text.split(",").map((s) => s.trim()).filter(Boolean);
      onChange(arr);
    } else {
      onChange(text);
    }
  };

  return (
    <div className={`relative w-full ${isOpen ? "z-[100]" : "z-10"}`} ref={dropdownRef}>
      <div className="relative flex items-center">
        <input
          type="text"
          placeholder={isMulti ? "value1, value2, value3" : "value..."}
          value={displayVal}
          onFocus={() => {
            setIsOpen(true);
            setFilterText("");
          }}
          onChange={handleInputChange}
          className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 placeholder-gray-400 dark:placeholder-gray-500 pr-8 shadow-sm"
        />
        <button
          type="button"
          onClick={() => {
            if (!isOpen && source && field) fetchValues();
            setIsOpen(!isOpen);
          }}
          className="absolute right-2 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white p-0.5"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500 dark:text-orange-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 rotate-90" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[200px] overflow-y-auto custom-scroll p-1 flex flex-col gap-0.5 animate-fade-in">
          {loading && (
            <div className="flex items-center justify-center py-3 text-xs text-gray-500 dark:text-gray-400 gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500 dark:text-orange-400" />
              <span>Fetching values...</span>
            </div>
          )}

          {!loading && filteredOptions.length === 0 && (
            <div className="py-2.5 px-3 text-xs text-gray-500 text-center italic">
              {source && field ? "No predefined values found. Type custom value." : "Select a field first"}
            </div>
          )}

          {!loading &&
            filteredOptions.map((o) => {
              const selected = isMulti
                ? Array.isArray(value) && value.includes(o.value)
                : value === o.value;
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => handleSelectOption(o.value)}
                  className={`w-full text-left text-xs px-3 py-1.5 rounded transition-colors flex items-center justify-between ${selected
                      ? "bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                >
                  <span className="truncate">{o.label}</span>
                  {selected && <span className="text-orange-500 dark:text-orange-400 font-bold text-xs">✓</span>}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── Multi-Column Selector Component for Raw Table Mode ──────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function MultiColumnSelector({
  getSourcesList,
  selectedColumns,
  onChange,
}: {
  getSourcesList: () => Array<{ source: string; label: string; prefix: string }>;
  selectedColumns: string[];
  onChange: (cols: string[]) => void;
}) {
  const [fields, setFields] = useState<
    Array<{
      key: string;
      label: string;
      displayLabel: string;
      source: string;
      sourceLabel: string;
      type: string;
      isJoined: boolean;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sourcesList = getSourcesList();
  const sourcesKey = sourcesList.map((s) => s.source).join(",");

  useEffect(() => {
    const fetchAllFields = async () => {
      if (!sourcesList.length) return;
      setLoading(true);
      try {
        const fieldResults: Array<{
          key: string;
          label: string;
          displayLabel: string;
          source: string;
          sourceLabel: string;
          type: string;
          isJoined: boolean;
        }> = [];

        await Promise.all(
          sourcesList.map(async (srcInfo, index) => {
            try {
              const res = await baseapi.get(`/cdash/meta/sources/${srcInfo.source}/fields`);
              const items = Array.isArray(res.data) ? res.data : [];
              const isJoined = index > 0;
              items.forEach((f: any) => {
                const key = isJoined ? `${srcInfo.source}.${f.name}` : f.name;
                const displayLabel = isJoined ? `${srcInfo.label} → ${f.label || f.name}` : (f.label || f.name);
                fieldResults.push({
                  key,
                  label: f.label || f.name,
                  displayLabel,
                  source: srcInfo.source,
                  sourceLabel: srcInfo.label,
                  type: f.type || "string",
                  isJoined,
                });
              });
            } catch {
              // ignore fetch failure
            }
          })
        );
        setFields(fieldResults);
      } finally {
        setLoading(false);
      }
    };

    fetchAllFields();
  }, [sourcesKey]);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const toggleColumn = (colKey: string) => {
    if (selectedColumns.includes(colKey)) {
      onChange(selectedColumns.filter((c) => c !== colKey));
    } else {
      onChange([...selectedColumns, colKey]);
    }
  };

  const filteredFields = useMemo(() => {
    if (!search.trim()) return fields;
    const q = search.toLowerCase();
    return fields.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        f.sourceLabel.toLowerCase().includes(q)
    );
  }, [fields, search]);

  const handleSelectAll = () => {
    const filteredKeys = filteredFields.map((f) => f.key);
    const combined = Array.from(new Set([...selectedColumns, ...filteredKeys]));
    onChange(combined);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm" ref={dropdownRef}>
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-450 flex items-center gap-2">
          <span>3 · Table Columns</span>
          <span className="bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-orange-500/20 dark:border-orange-500/30">
            {selectedColumns.length} Selected
          </span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-[10px] text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-bold hover:underline"
          >
            ＋ Select All
          </button>
          <span className="text-gray-300 dark:text-gray-700">|</span>
          <button
            type="button"
            onClick={handleClearAll}
            className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 font-semibold"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Selected column chips */}
      {selectedColumns.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-lg max-h-[140px] overflow-y-auto custom-scroll">
          {selectedColumns.map((colKey) => {
            const fieldObj = fields.find((f) => f.key === colKey);
            return (
              <span
                key={colKey}
                className="inline-flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-md text-[11px] font-medium group hover:border-orange-500/50 transition-colors shadow-sm"
              >
                {fieldObj?.isJoined && (
                  <span className="text-[9px] px-1 py-0.2 bg-purple-500/15 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300 rounded border border-purple-500/30 font-semibold">
                    {fieldObj.sourceLabel}
                  </span>
                )}
                <span>{fieldObj ? fieldObj.label : colKey}</span>
                <button
                  type="button"
                  onClick={() => toggleColumn(colKey)}
                  className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-0.5 ml-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="text-[11px] text-gray-500 italic p-3 bg-gray-50/50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-center">
          No columns selected. Pick fields from dropdown below or click "Select All" to display in table.
        </div>
      )}

      {/* Multi Select Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-gray-350 dark:hover:border-gray-600 text-left text-xs text-gray-900 dark:text-white px-3 py-2.5 rounded-lg font-semibold flex items-center justify-between transition-colors shadow-sm"
        >
          <span className="text-gray-700 dark:text-gray-300 truncate">
            {loading ? "Loading available fields..." : "＋ Add / Remove Table Columns..."}
          </span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-90" : ""}`} />
        </button>

        {dropdownOpen && (
          <div className="absolute top-[105%] left-0 right-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[280px] flex flex-col p-2 gap-2 animate-fade-in">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                placeholder="Search fields by name or label..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white pl-8 pr-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            <div className="overflow-y-auto custom-scroll flex-1 flex flex-col gap-1 pr-1">
              {loading ? (
                <div className="p-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                  Loading field schema...
                </div>
              ) : filteredFields.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-500">No matching fields found</div>
              ) : (
                filteredFields.map((f) => {
                  const isSelected = selectedColumns.includes(f.key);
                  return (
                    <label
                      key={f.key}
                      className={`flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs cursor-pointer select-none transition-colors ${isSelected
                          ? "bg-orange-500/10 border-orange-500/30 dark:border-orange-500/40 text-orange-700 dark:text-orange-300 font-semibold"
                          : "border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/70"
                        }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleColumn(f.key)}
                          className="rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-orange-500 focus:ring-0 focus:ring-offset-0 w-3.5 h-3.5 cursor-pointer accent-orange-500"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate text-gray-900 dark:text-white font-medium">{f.displayLabel}</span>
                          <span className="text-[10px] text-gray-500 font-mono truncate">{f.key}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {f.isJoined && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300 border border-purple-500/30 font-semibold">
                            {f.sourceLabel}
                          </span>
                        )}
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                          {f.type}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
